/**
 * PDF Template Utility
 * 
 * Common styling and structure for all PDFs in the application.
 * Use this to ensure consistent branding across all exports.
 * 
 * Usage:
 *   import { setupPDF, addHeader, addFooter, colors, fonts } from '../utils/pdfTemplate';
 *   
 *   const doc = setupPDF('portrait'); // or 'landscape'
 *   addHeader(doc, 'REPORT TITLE', 'Main Heading');
 *   // ... add your content ...
 *   addFooter(doc, { extraInfo: '15 gigs' });
 *   doc.save('filename.pdf');
 */

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import moment from 'moment';

// Export autoTable for use in other components
export { autoTable };

// ============================================
// BRAND COLORS
// ============================================
export const colors = {
  yellow: [212, 160, 23],      // #d4a017 - Primary accent
  darkGray: [51, 51, 51],      // #333333 - Main text
  mediumGray: [102, 102, 102], // #666666 - Medium text
  lightGray: [136, 136, 136],  // #888888 - Secondary text
  footerGray: [204, 204, 204], // #cccccc - Footer text
  orange: [203, 104, 0],       // #cb6800 - Warning/unpaid
  green: [34, 134, 58],        // #22863a - Success/paid
  blue: [37, 99, 235],         // #2563eb - Info/substitution
  white: [255, 255, 255],
  bgLight: [248, 248, 248],    // Light background for boxes
  bgAlt: [252, 252, 252],      // Alternate row background
};

// ============================================
// FONT SIZES
// ============================================
export const fonts = {
  title: 20,
  subtitle: 14,
  heading: 12,
  body: 9,
  small: 8,
  footer: 8,
  label: 8,
};

// ============================================
// LAYOUT CONSTANTS
// ============================================
export const layout = {
  margin: 18,
  accentBarHeight: 3,
  footerY: 10, // Distance from bottom
};

// ============================================
// SETUP PDF
// ============================================
/**
 * Create a new PDF document with standard settings
 * @param {'portrait' | 'landscape'} orientation 
 * @returns {jsPDF} Configured jsPDF instance
 */
export function setupPDF(orientation = 'portrait') {
  const doc = new jsPDF(orientation);
  return doc;
}

// ============================================
// GET PAGE DIMENSIONS
// ============================================
/**
 * Get page dimensions for the document
 * @param {jsPDF} doc 
 * @returns {{ pageWidth: number, pageHeight: number, margin: number, contentWidth: number }}
 */
export function getPageDimensions(doc) {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = layout.margin;
  const contentWidth = pageWidth - (margin * 2);
  return { pageWidth, pageHeight, margin, contentWidth };
}

// ============================================
// ADD YELLOW ACCENT BAR
// ============================================
/**
 * Add the yellow accent bar at the top of the page
 * @param {jsPDF} doc 
 */
export function addAccentBar(doc) {
  const { pageWidth } = getPageDimensions(doc);
  doc.setFillColor(...colors.yellow);
  doc.rect(0, 0, pageWidth, layout.accentBarHeight, 'F');
}

// ============================================
// ADD HEADER
// ============================================
/**
 * Add standard header with label and title
 * @param {jsPDF} doc 
 * @param {string} label - Small yellow text (e.g., "PAYMENT STATEMENT")
 * @param {string} title - Large bold text (e.g., "Outstanding Balance")
 * @param {string} [dateText] - Optional date text on the right
 * @returns {number} Y position after header (where content should start)
 */
export function addHeader(doc, label, title, dateText = null) {
  const { pageWidth, margin } = getPageDimensions(doc);
  
  // Add accent bar
  addAccentBar(doc);
  
  // Label (small, yellow)
  doc.setFontSize(fonts.body);
  doc.setTextColor(...colors.yellow);
  doc.text(label, margin, 18);
  
  // Title (large, bold, dark)
  doc.setFontSize(fonts.title);
  doc.setTextColor(...colors.darkGray);
  doc.setFont('helvetica', 'bold');
  doc.text(title, margin, 28);
  doc.setFont('helvetica', 'normal');
  
  // Date on the right (optional)
  if (dateText) {
    doc.setFontSize(fonts.body);
    doc.setTextColor(...colors.lightGray);
    doc.text(dateText, pageWidth - margin, 18, { align: 'right' });
  }
  
  return 36; // Y position where content can start
}

// ============================================
// ADD FOOTER
// ============================================
/**
 * Add standard footer with Esfincter branding
 * Format: Esfincter | Date • Username • v1.0.1
 * 
 * @param {jsPDF} doc 
 * @param {Object} options
 * @param {string} [options.username] - Username to display (required for consistency)
 * @param {string} [options.version] - Version string (default: "v1.0.1")
 */
export function addFooter(doc, options = {}) {
  const { pageWidth, pageHeight, margin } = getPageDimensions(doc);
  const { username = 'Unknown', version = 'v1.0.1' } = options;
  
  const footerY = pageHeight - layout.footerY;
  const printDate = moment().format('D MMM YYYY, HH:mm');
  
  doc.setFontSize(fonts.footer);
  doc.setTextColor(...colors.footerGray);
  
  // Left side - brand
  doc.text('Esfincter', margin, footerY);
  
  // Right side - date, username, version (matches backend gig PDF)
  const rightText = `${printDate}  •  ${username}  •  ${version}`;
  doc.text(rightText, pageWidth - margin, footerY, { align: 'right' });
}

// ============================================
// HELPER: DRAW INFO BOX
// ============================================
/**
 * Draw a rounded info box with label and value
 * @param {jsPDF} doc 
 * @param {number} x 
 * @param {number} y 
 * @param {number} width 
 * @param {number} height 
 * @param {string} label 
 * @param {string} value 
 * @param {Object} options
 * @param {number[]} [options.bgColor] - Background color
 * @param {number[]} [options.labelColor] - Label text color
 * @param {number[]} [options.valueColor] - Value text color
 */
export function drawInfoBox(doc, x, y, width, height, label, value, options = {}) {
  const {
    bgColor = colors.bgLight,
    labelColor = colors.lightGray,
    valueColor = colors.darkGray,
  } = options;
  
  // Background
  doc.setFillColor(...bgColor);
  doc.roundedRect(x, y, width, height, 2, 2, 'F');
  
  // Label
  doc.setFontSize(fonts.label);
  doc.setTextColor(...labelColor);
  doc.text(label, x + 8, y + 9);
  
  // Value
  doc.setFontSize(fonts.heading);
  doc.setTextColor(...valueColor);
  doc.setFont('helvetica', 'bold');
  doc.text(value, x + 8, y + 17);
  doc.setFont('helvetica', 'normal');
}

// ============================================
// HELPER: DRAW SUMMARY BOX WITH ACCENT
// ============================================
/**
 * Draw a summary box with colored left accent bar
 * @param {jsPDF} doc 
 * @param {number} x 
 * @param {number} y 
 * @param {number} width 
 * @param {number} height 
 * @param {number[]} accentColor - Color for left accent bar
 */
export function drawSummaryBox(doc, x, y, width, height, accentColor = colors.yellow) {
  // Background
  doc.setFillColor(...colors.bgLight);
  doc.roundedRect(x, y, width, height, 2, 2, 'F');
  
  // Accent bar on left
  doc.setFillColor(...accentColor);
  doc.rect(x, y, 3, height, 'F');
}

// ============================================
// HELPER: ADD PAYMENT INFO (IBAN)
// ============================================
/**
 * Add payment information section with IBAN
 * Only renders if IBAN is provided and not empty
 * @param {jsPDF} doc 
 * @param {string} iban - User's IBAN (if null/empty, nothing is rendered)
 * @param {number} [yPosition] - Optional Y position (default: auto-calculated above footer)
 * @returns {number} The Y position used (or 0 if nothing was rendered)
 */
export function addPaymentInfo(doc, iban, yPosition = null) {
  // Don't render if no IBAN
  if (!iban || iban.trim() === '') {
    return 0;
  }
  
  const { pageHeight, margin, contentWidth } = getPageDimensions(doc);
  
  // Calculate position - above footer with some padding
  const boxHeight = 22;
  const y = yPosition || (pageHeight - layout.footerY - boxHeight - 8);
  
  // Draw subtle box
  doc.setFillColor(...colors.bgLight);
  doc.roundedRect(margin, y, contentWidth, boxHeight, 2, 2, 'F');
  
  // Left accent bar (yellow)
  doc.setFillColor(...colors.yellow);
  doc.rect(margin, y, 3, boxHeight, 'F');
  
  // Label
  doc.setFontSize(fonts.label);
  doc.setTextColor(...colors.lightGray);
  doc.text('PAYMENT INFO', margin + 10, y + 7);
  
  // IBAN value
  doc.setFontSize(fonts.body);
  doc.setTextColor(...colors.darkGray);
  doc.setFont('courier', 'normal'); // Monospace for IBAN
  doc.text(`IBAN: ${iban}`, margin + 10, y + 15);
  doc.setFont('helvetica', 'normal'); // Reset font
  
  return y;
}

// ============================================
// TABLE STYLES (for jspdf-autotable)
// ============================================
export const tableStyles = {
  head: {
    fillColor: colors.yellow,
    textColor: colors.white,
    fontStyle: 'bold',
    fontSize: fonts.small,
    cellPadding: 4,
  },
  body: {
    textColor: colors.darkGray,
    fontSize: fonts.small,
    cellPadding: 3,
  },
  alternateRow: {
    fillColor: colors.bgAlt,
  },
  styles: {
    fontSize: fonts.small,
    overflow: 'ellipsize',
    lineColor: [240, 240, 240],
    lineWidth: 0.1,
  },
};

// ============================================
// MOBILE-FRIENDLY PDF SAVE
// ============================================

/**
 * Save PDF with mobile compatibility
 * Uses native share on mobile if available, otherwise downloads
 * 
 * @param {jsPDF} doc - The jsPDF document
 * @param {string} filename - Desired filename for the PDF
 */
export const savePDF = async (doc, filename) => {
  // Create blob from PDF
  const blob = doc.output('blob');
  
  // Check if we're on mobile and can use native share
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  
  if (isMobile && navigator.share && navigator.canShare) {
    // Try native share API (works well on iOS/Android)
    try {
      const file = new File([blob], filename, { type: 'application/pdf' });
      if (navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: filename,
        });
        return;
      }
    } catch (err) {
      // Share was cancelled or failed, fall through to download
      if (err.name !== 'AbortError') {
        console.log('Share failed, falling back to download');
      }
    }
  }
  
  // Fallback: Create download link
  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = filename;
  link.style.display = 'none';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
};

// ============================================
// EXPORT DEFAULT TEMPLATE CONFIG
// ============================================
const pdfTemplate = {
  colors,
  fonts,
  layout,
  setupPDF,
  getPageDimensions,
  addAccentBar,
  addHeader,
  addFooter,
  addPaymentInfo,
  drawInfoBox,
  drawSummaryBox,
  tableStyles,
  savePDF,
};

export default pdfTemplate;

